/**
 * useNotes — data + sync-state hook for Onthouden (spec §8.2).
 *
 * Sync-state truthfulness:
 * - every note/draft carries exactly one canonical SyncSaveState;
 * - a failed cloud write keeps the form data as a device-only draft
 *   ('rust-remember-draft', labelled 'Alleen op dit apparaat') and never
 *   claims a cloud save;
 * - queued/failed drafts retry automatically when the connection returns.
 *
 * All persistence goes through repositories.notes. When the cloud is not
 * configured the repository throws SUPABASE_NOT_CONFIGURED: the page shows an
 * honest 'cloud niet ingesteld' state and saves become explicit device-only
 * drafts — never a silent local-only fallback presented as sync.
 */
import { useCallback, useEffect, useRef, useState } from 'react'
import { toAppError } from '@/lib/supabase/errors'
import { repositories } from '@/services/repositories'
import { useSync } from '@/services/sync'
import type { AppError, NewNote, Note, SyncSaveState } from '@/types'
import {
  draftKeyFor,
  loadNoteDrafts,
  removeNoteDraft,
  saveNoteDraft,
  writeNoteDrafts,
  type NoteDraft,
  type NoteDraftMap,
} from './drafts'

export type NoteFormValues = {
  title: string
  content: string
  category: string
  pinned: boolean
  urgent: boolean
}

export type SaveNoteInput = {
  noteId: string | null
  values: NoteFormValues
}

export type SaveNoteResult =
  | { ok: true }
  | { ok: false; error: AppError; state: SyncSaveState }

export type NotesLoadStatus = 'loading' | 'ready' | 'error'

function toNewNote(values: NoteFormValues, existing?: Note): NewNote {
  return {
    title: values.title.trim(),
    content: values.content.trim(),
    category: values.category,
    pinned: values.pinned,
    urgent: values.urgent,
    done: existing?.done ?? false,
    archived: existing?.archived ?? false,
  }
}

function draftStateFor(error: AppError, online: boolean): SyncSaveState {
  if (!online) return 'sync_queued'
  if (error.code === 'SUPABASE_NOT_CONFIGURED') return 'local_saved'
  return 'sync_failed'
}

export function useNotes() {
  const { online, pendingWrites } = useSync()
  const [notes, setNotes] = useState<Note[]>([])
  const [status, setStatus] = useState<NotesLoadStatus>('loading')
  const [error, setError] = useState<AppError | null>(null)
  const [syncStates, setSyncStates] = useState<Record<string, SyncSaveState>>({})
  const [drafts, setDrafts] = useState<NoteDraftMap>(() => loadNoteDrafts())
  const retryingRef = useRef(false)

  const setNoteState = useCallback((noteId: string, state: SyncSaveState) => {
    setSyncStates((previous) => ({ ...previous, [noteId]: state }))
  }, [])

  const refresh = useCallback(async () => {
    setStatus('loading')
    setError(null)
    try {
      // Include archived so the 'Gearchiveerd' filter and restore work.
      const list = await repositories.notes.list(true)
      setNotes(list)
      setSyncStates((previous) => {
        const next: Record<string, SyncSaveState> = {}
        for (const note of list) {
          // Keep an explicit failure visible instead of silently upgrading it.
          next[note.id] = previous[note.id] === 'sync_failed' ? 'sync_failed' : 'cloud_saved'
        }
        return next
      })
      setStatus('ready')
    } catch (loadError) {
      setError(toAppError(loadError, 'database'))
      setStatus('error')
    }
  }, [])

  const refreshSilently = useCallback(async () => {
    try {
      const list = await repositories.notes.list(true)
      setNotes(list)
    } catch {
      // Keep the current list; the next explicit refresh surfaces the error.
    }
  }, [])

  useEffect(() => {
    void refresh()
  }, [refresh])

  const saveNote = useCallback(
    async (input: SaveNoteInput): Promise<SaveNoteResult> => {
      const existing = input.noteId ? notes.find((note) => note.id === input.noteId) : undefined
      const data = toNewNote(input.values, existing)
      if (input.noteId) setNoteState(input.noteId, 'syncing')
      try {
        const saved = input.noteId
          ? await repositories.notes.update(input.noteId, data)
          : await repositories.notes.create(data)
        setNoteState(saved.id, 'cloud_saved')
        setDrafts(removeNoteDraft(draftKeyFor(input.noteId)))
        await refreshSilently()
        return { ok: true }
      } catch (saveError) {
        const appError = toAppError(saveError, 'database')
        const state = draftStateFor(appError, online)
        if (input.noteId) setNoteState(input.noteId, state)
        const draft: NoteDraft = {
          noteId: input.noteId,
          title: data.title,
          content: data.content,
          category: data.category,
          pinned: data.pinned,
          urgent: data.urgent,
          state,
          updatedAt: new Date().toISOString(),
        }
        setDrafts(saveNoteDraft(draftKeyFor(input.noteId), draft))
        return { ok: false, error: appError, state }
      }
    },
    [notes, online, refreshSilently, setNoteState],
  )

  /** Toggle-style updates (pin / urgent / done / archive / restore). */
  const updateFlags = useCallback(
    async (note: Note, patch: Partial<Pick<Note, 'pinned' | 'urgent' | 'done' | 'archived'>>): Promise<boolean> => {
      setNoteState(note.id, 'syncing')
      try {
        const updated = await repositories.notes.update(note.id, patch)
        setNotes((previous) => previous.map((item) => (item.id === updated.id ? updated : item)))
        setNoteState(updated.id, 'cloud_saved')
        return true
      } catch {
        setNoteState(note.id, 'sync_failed')
        return false
      }
    },
    [setNoteState],
  )

  const deleteNote = useCallback(
    async (noteId: string): Promise<boolean> => {
      setNoteState(noteId, 'syncing')
      try {
        await repositories.notes.remove(noteId)
        setNotes((previous) => previous.filter((note) => note.id !== noteId))
        setSyncStates((previous) => {
          const next = { ...previous }
          delete next[noteId]
          return next
        })
        setDrafts(removeNoteDraft(noteId))
        return true
      } catch {
        setNoteState(noteId, 'sync_failed')
        return false
      }
    },
    [setNoteState],
  )

  const persistDraft = useCallback((draft: NoteDraft) => {
    setDrafts(saveNoteDraft(draftKeyFor(draft.noteId), draft))
  }, [])

  const discardDraft = useCallback((key: string) => {
    setDrafts(removeNoteDraft(key))
  }, [])

  const setDraftState = useCallback((key: string, state: SyncSaveState) => {
    const current = loadNoteDrafts()
    const draft = current[key]
    if (!draft) return
    current[key] = { ...draft, state, updatedAt: new Date().toISOString() }
    writeNoteDrafts(current)
    setDrafts(current)
  }, [])

  const retryDraft = useCallback(
    async (key: string): Promise<boolean> => {
      const draft = loadNoteDrafts()[key]
      if (!draft) return false
      setDraftState(key, 'syncing')
      try {
        const existing = draft.noteId ? notes.find((note) => note.id === draft.noteId) : undefined
        const data = toNewNote(
          {
            title: draft.title,
            content: draft.content,
            category: draft.category,
            pinned: draft.pinned,
            urgent: draft.urgent,
          },
          existing,
        )
        const saved = draft.noteId
          ? await repositories.notes.update(draft.noteId, data)
          : await repositories.notes.create(data)
        setNoteState(saved.id, 'cloud_saved')
        setDrafts(removeNoteDraft(key))
        await refreshSilently()
        return true
      } catch (retryError) {
        const appError = toAppError(retryError, 'database')
        setDraftState(key, draftStateFor(appError, online))
        return false
      }
    },
    [notes, online, refreshSilently, setDraftState, setNoteState],
  )

  const retryAllDrafts = useCallback(async () => {
    if (retryingRef.current) return
    retryingRef.current = true
    try {
      for (const key of Object.keys(loadNoteDrafts())) {
        // Sequential on purpose: one in-flight write per draft, fresh read each time.
        await retryDraft(key)
      }
    } finally {
      retryingRef.current = false
    }
  }, [retryDraft])

  // Draft retries on reconnect + one attempt on mount when online.
  const wasOnlineRef = useRef(online)
  useEffect(() => {
    const wasOnline = wasOnlineRef.current
    wasOnlineRef.current = online
    if (online && !wasOnline && Object.keys(loadNoteDrafts()).length > 0) {
      void retryAllDrafts()
    }
  }, [online, retryAllDrafts])

  const mountedRetryRef = useRef(false)
  useEffect(() => {
    if (mountedRetryRef.current) return
    mountedRetryRef.current = true
    if (online && Object.keys(loadNoteDrafts()).length > 0) {
      void retryAllDrafts()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return {
    notes,
    status,
    error,
    syncStates,
    drafts,
    online,
    pendingWrites,
    refresh,
    saveNote,
    updateFlags,
    deleteNote,
    persistDraft,
    discardDraft,
    retryDraft,
  }
}
